import { createFileRoute, redirect, useLocation } from "@tanstack/react-router";

import {
  ConnectAccount,
  ConnectAccountClientCredentials,
  ConnectAccountCredentials,
  ConnectAccountManual,
  ConnectAccountOcpp,
  ConnectApiKey,
  ConnectMethod,
  ConnectPeblar,
  EligibilityCheck,
  Finish,
  LinkCharger,
  LinkChargerManual,
  LinkChargerOcpp,
  ProviderSelection,
  ProviderType,
  Register,
  SessionMethod,
  UnsupportedCharger,
} from "~/components/onboarding";
import { montaInstructions } from "~/components/onboarding/ConnectAccountClientCredentials";
import { authClient } from "~/lib/auth";
import { BRANCH_FLOWS, connectAccountStep } from "./-journey";

const VALID_STEPS = Object.values(BRANCH_FLOWS).flat();

const parseOnboardingSearch = (
  search: Record<string, unknown>,
): {
  provider?: string;
  model?: string;
  via?: string;
  type?: string;
  error?: string;
  referralId?: string;
  partner?: string;
  chargePointId?: string;
} => ({
  provider: (search.provider as string) || undefined,
  model: (search.model as string) || undefined,
  via: (search.via as string) || undefined,
  type: (search.type as string) || undefined,
  error: (search.error as string) || undefined,
  referralId: (search.referralId as string) || undefined,
  partner: (search.partner as string) || undefined,
  chargePointId: (search.chargePointId as string) || undefined,
});

export const Route = createFileRoute("/(auth-pages)/onboarding/$step")({
  validateSearch: parseOnboardingSearch,
  beforeLoad: async ({ params, search }) => {
    // link-charger is normally reached only after register. If a user lands
    // here still anonymous (e.g. OAuth callback with an invalid token /
    // consent not actually linked, so onLinkAccount never fired), restart
    // from the beginning rather than letting the charger-link API call fail.
    if (params.step === "link-charger") {
      const { data: session } = await authClient.getSession({
        query: { disableCookieCache: true },
      });
      if (!session || session.user.isAnonymous) {
        throw redirect({
          to: "/onboarding/$step",
          params: { step: "provider-selection" },
          // Carry the OAuth callback's error (e.g. consent_denied, no_session)
          // through the restart, so it isn't silently swallowed.
          search: { error: search.error },
          replace: true,
        });
      }
      return;
    }

    // Skip register entirely when the user is already fully signed in.
    // Anonymous users (incl. abandoned signups with a real email but no OTP
    // verify) still need to go through register.
    if (params.step !== "register") return;
    const { data: session } = await authClient.getSession({
      query: { disableCookieCache: true },
    });
    if (!session || session.user.isAnonymous) return;
    const { via, provider, type, referralId, partner } = search;

    throw redirect({
      to: "/onboarding/$step",
      params: { step: connectAccountStep(via, provider) },
      search: { provider, via, type, referralId, partner },
      replace: true,
    });
  },
  component: OnboardingStep,
});

function OnboardingStep() {
  // Route.useParams() and Route.useSearch() lag one render behind during a
  // TanStack Router transition; useLocation() always reflects the current URL.
  const location = useLocation();
  const step =
    VALID_STEPS.find((s) => location.pathname.endsWith(s)) ?? "provider-selection";
  const { provider, model, via, type, error, referralId, partner, chargePointId } =
    parseOnboardingSearch(location.search);
  // Show-once OCPP token, carried in history state so it never reaches the URL.
  const ocppAuthToken = location.state.ocppAuthToken;

  // Key forces React to fully unmount the old step and mount the new one,
  // which cancels any pending queries and clears all component state.
  switch (step) {
    case "provider-selection":
      return (
        <ProviderSelection
          key="provider-selection"
          error={error}
          referralId={referralId}
          partner={partner}
        />
      );
    case "connect-method":
      return (
        <ConnectMethod
          key="connect-method"
          provider={provider ?? ""}
          model={model}
          referralId={referralId}
          partner={partner}
        />
      );
    case "provider-type":
      return (
        <ProviderType
          key="provider-type"
          provider={provider ?? ""}
          model={model}
          referralId={referralId}
          partner={partner}
        />
      );
    case "session-method":
      return (
        <SessionMethod
          key="session-method"
          provider={provider ?? ""}
          referralId={referralId}
          partner={partner}
          type={type}
        />
      );
    case "unsupported-charger":
      return <UnsupportedCharger key="unsupported-charger" type={type} />;
    case "eligibility-check":
      return (
        <EligibilityCheck
          key="eligibility-check"
          referralId={referralId}
          provider={provider ?? ""}
          partner={partner}
        />
      );
    case "register":
      return (
        <Register
          key="register"
          provider={provider ?? ""}
          referralId={referralId}
          partner={partner}
        />
      );
    case "connect-account":
      return <ConnectAccount key="connect-account" provider={via ?? provider ?? ""} />;
    case "connect-account-apikey":
      return <ConnectApiKey key="connect-account-apikey" provider={provider ?? ""} />;
    case "connect-account-peblar":
      return <ConnectPeblar key="connect-account-peblar" provider={provider ?? ""} />;
    case "connect-account-bluecurrent":
      // Blue Current is a direct brand that authenticates with email + password,
      // so it reuses the shared credentials screen with no `via` — credentials
      // are posted to /auth/sign-in/bluecurrent.
      return (
        <ConnectAccountCredentials
          key="connect-account-bluecurrent"
          provider={provider ?? ""}
        />
      );
    case "connect-account-credentials":
      // Monta authenticates with an OAuth application's Client ID + Client
      // Secret rather than an email/password, so it uses the dedicated
      // client-credentials screen with provider-specific instructions.
      if (via === "monta-public") {
        return (
          <ConnectAccountClientCredentials
            key="connect-account-credentials"
            provider={provider ?? ""}
            via={via}
          >
            <ol className="list-decimal space-y-1 pl-4">{montaInstructions()}</ol>
          </ConnectAccountClientCredentials>
        );
      }
      return (
        <ConnectAccountCredentials
          key="connect-account-credentials"
          provider={provider ?? ""}
          via={via}
        />
      );
    case "connect-account-manual":
      return (
        <ConnectAccountManual key="connect-account-manual" provider={provider ?? ""} />
      );
    case "connect-account-ocpp":
      return <ConnectAccountOcpp key="connect-account-ocpp" provider={provider ?? ""} />;
    case "link-charger":
      return (
        <LinkCharger
          key="link-charger"
          error={error}
          provider={provider ?? ""}
          via={via}
        />
      );
    case "link-charger-manual":
      return <LinkChargerManual key="link-charger-manual" provider={provider ?? ""} />;
    case "link-charger-ocpp":
      return (
        <LinkChargerOcpp
          key="link-charger-ocpp"
          provider={provider}
          type={type}
          chargePointId={chargePointId}
          ocppAuthToken={ocppAuthToken}
        />
      );
    case "finish":
      return <Finish key="finish" />;
    default:
      return null;
  }
}
